// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, HostListener, OnChanges, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Deal, DealStatuses, JacketType } from '@interfaces/deal.interfaces';
import {
    DdjDocument,
    DealDocuments,
    DocumentStatus,
    IdVerification,
    FormDocumentUploadType,
    InternalDocumentTypeIds,
    Assignees,
    AssigneeResolver,
    IdCheckStatus,
} from '@interfaces/document.interfaces';
import { DocumentsService } from '@services/documents.service';
import { UserService } from '@services/user.service';
import { catchError, firstValueFrom, map, mergeAll, mergeMap, of, switchMap, takeWhile } from 'rxjs';
import { DeleteDealModalComponent } from 'src/app/modals/delete-deal-modal/delete-deal-modal.component';
import { isMobileOrTablet, showMissingImage } from 'src/app/utils/utils';
import { DealsService } from '@services/deals.service';
import { PermissionsService } from '@services/permissions.service';
import { ErrorService } from '@services/error.service';
import { DocActions, User, UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { NavService } from '@services/nav.service';
import { AuditTrailModalComponent } from 'src/app/modals/audit-trail-modal/audit-trail-modal.component';
import { DocumentStatusModalComponent } from 'src/app/modals/document-status-modal/document-status-modal.component';
import { FinalDocModalComponent } from 'src/app/modals/final-doc-modal/final-doc-modal.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { getIdVerificationClass, isBuyerIdentification, isCoBuyerIdentification, isIdentification } from 'src/app/utils/document-utils';
import { InternalDocumentInterventionModalComponent } from 'src/app/modals/internal-document-intervention-modal/internal-document-intervention-modal.component';
import { R1JacketModalComponent } from 'src/app/modals/r1-jacket-modal/r1-jacket-modal.component';
import { VendorService } from '@services/vendor.service';
import { MobileOnlyUploadModalComponent } from 'src/app/modals/mobile-only-upload-modal/mobile-only-upload-modal.component';
import { FeatureFlagService } from '@services/feature-flag.service';
import { LightweightPdfViewModalComponent } from 'src/app/modals/lightweight-pdf-view-modal/lightweight-pdf-view-modal.component';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { DealershipService } from '../../../services/dealership.service';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { AddCobuyerModalComponent } from 'src/app/modals/add-cobuyer-modal/add-cobuyer-modal.component';
import { SuccessModalService } from '@services/success-modal.service';
import { MultiSelectModalComponentProps } from '@interfaces/interfaces';
import { MultiSelectModalComponent } from 'src/app/modals/multi-select-modal/multi-select-modal.component';
import { DownloadService } from '@services/download.service';
import { Features } from '@interfaces/dealer.interfaces';
import { MatMenuTrigger } from '@angular/material/menu';
import { EditNameModalComponent } from 'src/app/modals/edit-name-modal/edit-name-modal.component';
import { SettingsService } from '@services/settings.service';
import { EditNotesModalComponent } from 'src/app/modals/edit-notes-modal/edit-notes-modal.component';
import { RpcApiResponse } from '@interfaces/api.interfaces';
import { SnackbarService } from '@services/snackbar.service';
import { EventsService } from '@services/events.service';

@Component({
    selector: 'deal',
    templateUrl: './deal.component.html',
    styleUrls: ['./deal.component.scss'],
})
export class DealComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @ViewChild('helpPanel') helpPanel: TemplateRef<any>;
    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
    public deal: Deal = null;
    public dealDocs: DealDocuments = null;
    private isComponentAlive = true;
    public isLoading = false;
    public user: User = null;
    public finalizeDocumentsEnabled = false;
    // TODO: Remove once we restrict deals to only one of each DL type //
    public allIdVerificationResults: IdVerification[] = [];
    public buyerIdVerificationResults: IdVerification = null;
    public cobuyerIdVerificationResults: IdVerification = null;
    public showSubmitForReviewForBuyer = false;
    public showSubmitForReviewForCoBuyer = false;
    public disableSubmitForReview = true;
    tenantSupportPhone = '';
    protected dealType = DealStatuses;
    protected DocActions = DocActions;
    public canSendBuyerNotification = false;
    public canSendCoBuyerNotification = false;
    public isDesktop: boolean;
    private idVerificationUtilizationComplete: boolean;
    private idVerificationResultsComplete: boolean;
    private assigneeResolver: AssigneeResolver;
    readonly DealStatus = DealStatuses;

    constructor(
        private readonly dialog: MatDialog,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly dealService: DealsService,
        private readonly deviceService: DeviceDetectorService,
        private readonly documentsService: DocumentsService,
        private readonly errorService: ErrorService,
        private readonly eventService: EventsService,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly vendorService: VendorService,
        private readonly permissionsService: PermissionsService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly sharedService: SharedService,
        private readonly dealershipService: DealershipService,
        private readonly successModalService: SuccessModalService,
        private readonly downloadService: DownloadService,
        private readonly settingsService: SettingsService,
        private readonly snackbarService: SnackbarService
    ) {
        this.isDesktop = !isMobileOrTablet();
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.isDesktop = !isMobileOrTablet();
    }

    ngOnInit(): void {
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            const { supportPhone } = await this.settingsService.getOemSettings(dealer.oemCode);
            this.tenantSupportPhone = supportPhone;
        });

        this.finalizeDocumentsEnabled = this.documentsService.finalizeEnabledForDealer();
        this.deal = this.route.snapshot.data.deal;
        this.sortDocumentQueues(this.route.snapshot.data.documents);
        this.user = this.route.snapshot.data.user;

        this.checkDealStatus();

        this.navService.setTitle(`Deal #${this.deal.dealId}`);

        if (this.dealDocs.complete.some((doc) => isIdentification(doc.internalDocumentTypeId))) {
            this.isLoading = true;
            this.getIdVerificationResults();
            this.getIdVerificationUtilization();
        } else {
            this.idVerificationResultsComplete = true;
            this.idVerificationUtilizationComplete = true;
        }

        if (this.deal.buyer.userId) {
            this.checkUserLoginStatus(this.deal.buyer.userId, 'buyer');
        }

        if (this.deal.coBuyer.userId) {
            this.checkUserLoginStatus(this.deal.coBuyer.userId, 'cobuyer');
        }

        this.updateLabels();
    }

    ngOnChanges() {
        this.checkDealStatus();
    }

    ngAfterViewInit(): void {
        this.isComponentAlive = true;
        this.eventService.build().then(() => {
            this.eventService.subscribeToDeal(this.deal.dealId);
            this.eventService.onDocumentQueueUpdate((update) => {
                // someone else updated a doc, so we need to update our data
                console.log(`Document ${update.documentId} modified by an external session (another user, or browser).  Updating our data...`);
                this.reloadDocs();
            });
        });

        this.navService.setHelpPanel(this.helpPanel);
        this.assigneeResolver = new AssigneeResolver(
            this.user.userType.toString(),
            this.user.userId.toString(),
            this.user.email,
            this.user.dealerId.toString(),
            this.deal.salesperson.email,
            this.deal.dealerId.toString(),
            this.deal.buyer.userId.toString(),
            this.deal.coBuyer?.userId?.toString()
        );
    }

    async ngOnDestroy(): Promise<void> {
        this.navService.setHelpPanel(null);
        this.isComponentAlive = false;
        await this.eventService.unsubscribeFromDeal(this.deal.dealId);
        this.eventService.offDocumentQueueUpdate();
    }

    get isInternalUser(): boolean {
        return this.user.userType === UserTypes.Internal;
    }

    get hasCoBuyer(): boolean {
        return this.deal?.coBuyer?.userId ? true : false;
    }

    get hasCoBuyerDocuments(): boolean {
        return this.dealDocs?.cobuyer?.length > 0;
    }

    sortDocumentQueues(documents: DealDocuments): void {
        // DDJ-3936: Sort buyer/cobuyer documents with eSign at the top
        documents.buyer = documents.buyer.sort((a, b) => {
            if (a.internalDocumentTypeId === InternalDocumentTypeIds.BuyerESignConsent) return -1;
            return a.displayName.localeCompare(b.displayName);
        });
        documents.cobuyer = documents.cobuyer.sort((a, b) => {
            if (a.internalDocumentTypeId === InternalDocumentTypeIds.CobuyerESignConsent) return -1;
            return a.displayName.localeCompare(b.displayName);
        });

        this.dealDocs = documents;
    }

    private checkUserLoginStatus(userId: number, type: 'buyer' | 'cobuyer') {
        this.userService
            .getUserByUserId$(this.deal.dealerId, userId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                if (user) {
                    //check firstSuccessfullLogon date to see if the user has every successfully logged on including successfull MFA
                    if (type === 'buyer') {
                        this.canSendBuyerNotification = user.firstSuccessfulLogon === null ? false : true;
                    } else {
                        this.canSendCoBuyerNotification = user.firstSuccessfulLogon === null ? false : true;
                    }
                }
            });
    }

    setCanResendLink() {
        if (this.deal.buyer.userId !== null) {
            this.checkUserLoginStatus(this.deal.buyer.userId, 'buyer');
        }

        if (this.deal.coBuyer.userId != null) {
            this.checkUserLoginStatus(this.deal.coBuyer.userId, 'cobuyer');
        }
    }

    private checkDealStatus() {
        this.setCanResendLink();
    }

    public swapBuyerAndCoBuyer(): void {
        this.isLoading = true;

        this.dealService
            .swapBuyerAndCoBuyer(this.deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((result) => {
                if (result) {
                    this.deal = result;
                    this.isLoading = false;
                    this.checkDealStatus();

                    this.snackbarService.openSnackbar('success', 'You have successfully swapped the Buyer and Co-Buyer.');

                    this.refreshDeal(this.deal.dealId);
                } else {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                }
            });
    }

    canArchive(withStatus: boolean): boolean {
        if (this.deal && this.user) {
            if (withStatus) {
                return this.permissionsService.canCloseArchiveFinalizeDeal(
                    this.deal.statusId,
                    [DealStatuses.Completed, DealStatuses.Archived],
                    this.deal.jacketTypeId
                );
            } else {
                return this.permissionsService.hasPermission(UserPermissionIds.DealsFinalizeAndClose);
            }
        } else {
            return false;
        }
    }

    routeOneEnabled(): boolean {
        return this.isRouteOneEnabled;
    }

    get isOpened(): boolean {
        return this.deal.statusId === DealStatuses.Open;
    }

    get isClosed(): boolean {
        return this.deal.statusId === DealStatuses.Completed;
    }

    get isArchived(): boolean {
        return this.deal.statusId === DealStatuses.Archived;
    }

    get isDeleted(): boolean {
        return this.deal.statusId === DealStatuses.Deleted;
    }

    canChangeIsDealerOnly(doc: DdjDocument) {
        return !(isIdentification(doc.internalDocumentTypeId) || doc.internalDocumentTypeId === InternalDocumentTypeIds.IdVerificationResults);
    }

    canModifyDocuments(withStatus: boolean): boolean {
        if (this.deal && this.user) {
            if (withStatus) {
                return this.permissionsService.canModifyDocuments(this.deal.statusId, [DealStatuses.Open, DealStatuses.Completed], this.deal.jacketTypeId);
            } else {
                return this.permissionsService.hasPermission(UserPermissionIds.Deals);
            }
        } else {
            return false;
        }
    }

    canModifyNotes(): boolean {
        if (this.deal && this.user) {
            return this.permissionsService.hasPermission(UserPermissionIds.Deals);
        } else {
            return false;
        }
    }

    get canDownloadDocuments(): boolean {
        if (this.dealDocs && this.user) {
            return true;
        }

        return false;
    }

    canDeleteDeal(withStatus: boolean): boolean {
        if (withStatus) {
            return this.permissionsService.canDeleteDeal(this.deal.statusId, [DealStatuses.Open], this.deal.jacketTypeId);
        } else {
            return this.permissionsService.hasPermission(UserPermissionIds.DealsDelete);
        }
    }

    // Dealer can close a deal whenever they would like
    canClose(withStatus: boolean): boolean {
        if (this.deal && this.user && this.dealDocs) {
            if (withStatus) {
                return this.permissionsService.canCloseArchiveFinalizeDeal(this.deal.statusId, [DealStatuses.Open], this.deal.jacketTypeId);
            } else {
                return this.permissionsService.hasPermission(UserPermissionIds.DealsFinalizeAndClose);
            }
        } else {
            return false;
        }
    }

    public canReopen = (): boolean => {
        if (this.deal.statusId !== DealStatuses.Completed) {
            return false;
        }
        if (!this.permissionsService.hasPermission(UserPermissionIds.DealsFinalizeAndClose)) {
            return false;
        }
        return true;
    };

    get canRestore(): boolean {
        if (this.deal.statusId !== DealStatuses.Deleted) {
            return false;
        }
        if (!this.permissionsService.hasPermission(UserPermissionIds.DealsDelete)) {
            return false;
        }
        return true;
    }

    public reopenDeal = (): void => {
        this.isLoading = true;
        this.dealService
            .reopenDeal$(this.deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                }),
                switchMap(() => this.dealService.getDeal$(this.deal.dealId, true))
            )
            .subscribe((deal) => {
                this.deal = deal;
                this.updateLabels();
                this.snackbarService.openSnackbar(
                    'success',
                    `The jacket for <b>${this.deal.buyer.firstName + ' ' + this.deal.buyer.lastName}</b> has been reopened.`
                );
                this.isLoading = false;
            });
    };

    public restoreDeal = (): void => {
        this.isLoading = true;
        this.dealService
            .restoreDeal$(this.deal.dealId, JacketType.Deal)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((result) => {
                this.dealService.getDeal$(result.dealId, true).subscribe((deal) => {
                    this.deal = deal;
                    this.updateLabels();
                    this.isLoading = false;

                    this.snackbarService.openSnackbar(
                        'success',
                        `The jacket for <strong>${result.buyer.firstName + ' ' + result.buyer.lastName}</strong> has been restored.`
                    );
                });
            });
    };

    get isInternalWithAdminPermission(): boolean {
        if (this.deal && this.user && this.dealDocs) {
            return this.userService.hasPermission$([UserTypes.Internal], [UserPermissionIds.DDJAdmin]);
        } else {
            return false;
        }
    }

    //Status 18 = EnqueueCertification
    get helpMessageStatusEnqueueCertification(): string {
        if (this.isInternalWithAdminPermission) {
            return 'Applying digital certificate to document. Document will be moved to Completed after the process is done. If you believe the document is stuck in the status please take action.';
        }

        return null;
    }

    //Status 20 = InterventionRequired
    get helpMessageStatusInterventionRequired(): string {
        if (this.isInternalWithAdminPermission) {
            return 'Document in status Intervention Required. Action is required.';
        } else {
            return 'Failed applying Digital Certificate. Please contact the help desk.';
        }
    }

    get isRouteOneEnabled(): boolean {
        return this.featureFlagService.isDealerFeatureEnabled(this.user?.dealerId, Features.RouteOne);
    }

    get isDealerSigningOutOfQueueEnabled(): boolean {
        const test = this.featureFlagService.isFeatureEnabled(Features.DealerCanSignOutOfQueue);
        return test;
    }

    get getR1MessageForSyncButton(): string {
        return this.isRouteOneEnabled ? 'Click here to retrieve documents from RouteOne or other supported E-Contract System.' : '';
    }

    get getR1MessageForUploadButton(): string {
        return this.isRouteOneEnabled ? 'Click here to add documents to your E-Contract in RouteOne or other supported E-Contract System.' : '';
    }

    get canAccessNotarization(): boolean {
        return this.featureFlagService.isDealerFeatureEnabled(this.user?.dealerId, Features.Notarize);
    }

    showArchive() {
        return this.deal.statusId === 2 || this.deal.statusId === 3;
    }

    updateLabels() {
        this.setPageTitle();
    }

    setPageTitle() {
        this.navService.pageTitle.next(
            `Jacket ID ${this.deal.dealId} (Deal # ${this.deal.dealerDealNumber || 'N/A'}) - ${this.deal.buyer.firstName} ${this.deal.buyer.lastName}`
        );
    }

    getIdVerificationResults() {
        this.isLoading = true;
        this.idVerificationResultsComplete = false;
        this.documentsService
            .getIdVerifications$(this.deal.dealId, this.deal.dealerId)
            .pipe(takeWhile(() => this.isComponentAlive === true))
            .subscribe((results) => {
                results.sort((a, b) => {
                    return b.documentId - a.documentId;
                });
                this.allIdVerificationResults = results;

                this.buyerIdVerificationResults = results.find((dl) => isBuyerIdentification(dl.internalDocumentTypeId)) || null;
                this.cobuyerIdVerificationResults = results.find((dl) => isCoBuyerIdentification(dl.internalDocumentTypeId)) || null;

                // if there's no validation results document, the license can be submitted for validation
                this.showSubmitForReviewForBuyer =
                    null === this.buyerIdVerificationResults?.resultsDocumentId ||
                    IdCheckStatus.ReadyForSubmit === this.buyerIdVerificationResults?.verificationStatus;
                this.showSubmitForReviewForCoBuyer =
                    null === this.cobuyerIdVerificationResults?.resultsDocumentId ||
                    IdCheckStatus.ReadyForSubmit === this.cobuyerIdVerificationResults?.verificationStatus;

                this.idVerificationResultsComplete = true;
                // if we have both results and utilization, we can stop the spinner
                if (this.idVerificationUtilizationComplete) {
                    this.isLoading = false;
                }
            });
    }

    getIdVerificationUtilization() {
        this.isLoading = true;
        this.idVerificationUtilizationComplete = false;
        this.disableSubmitForReview = true;

        this.documentsService
            .getDealerIdVerificationScanStatus(this.deal.dealerId)
            .pipe(takeWhile(() => this.isComponentAlive === true))
            .subscribe((scanningEnabled) => {
                this.disableSubmitForReview = !scanningEnabled;

                this.idVerificationUtilizationComplete = true;
                // if we have both results and utilization, we can stop the spinner
                if (this.idVerificationResultsComplete) {
                    this.isLoading = false;
                }
            });
    }

    checkForAdditionalStyles(doc: DdjDocument) {
        if (doc.internalDocumentTypeId === InternalDocumentTypeIds.IdVerificationResults) {
            //Since there (for now) can be multiple dl's of the same type, try our best to match the color on this doc with
            //the correct verification result (even if its not what's shown in the customer card)
            const verif = this.allIdVerificationResults.find((r) => r.resultsDocumentId == doc.documentId);
            return getIdVerificationClass(verif);
        }
        return null;
    }

    closeDeal(): void {
        this.isLoading = true;

        // force deal to close even if documents need to be completed
        this.dealService
            .closeDeal$(this.deal.dealId, this.deal.jacketTypeId, true)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((response) => {
                    if (response?.error?.statusCode === 450) {
                        response.error.title = response.error.name;
                        this.errorService.showError(response?.error);
                        this.isLoading = false;
                        throw response;
                    } else {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw response;
                    }
                }),
                switchMap((result: string) => (result && result.length > 0 ? this.dealService.getDeal$(this.deal.dealId, true) : of(null)))
            )
            .subscribe((deal) => {
                if (deal) {
                    this.deal = deal;
                    this.updateLabels();
                    this.snackbarService.openSnackbar(
                        'success',
                        `The jacket for <b>${this.deal.buyer.firstName + ' ' + this.deal.buyer.lastName}</b> has been closed.`
                    );
                }

                this.isLoading = false;
            });
    }

    archiveDeal(archive: boolean): void {
        this.isLoading = true;
        this.dealService
            .archiveDeal$(this.deal.dealId, this.deal.dealerId, this.deal.jacketTypeId, archive)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((response) => {
                    if (response?.error?.statusCode === 450) {
                        response.error.title = response.error.name;
                        this.errorService.showError(response?.error);
                        this.isLoading = false;
                        throw response;
                    } else {
                        this.errorService.showGenericError();
                        this.isLoading = false;
                        throw response;
                    }
                })
            )
            .subscribe((deal: Deal) => {
                this.deal = deal;
                this.updateLabels();
                this.snackbarService.openSnackbar(
                    'success',
                    `The jacket for <b>${this.deal.buyer.firstName + ' ' + this.deal.buyer.lastName}</b> has been ${archive ? 'archived' : 'unarchived'}.`
                );

                this.isLoading = false;
            });
    }

    dealDelete(): void {
        if (this.canDeleteDeal(true)) {
            this.dialog
                .open(DeleteDealModalComponent, {
                    width: '700px',
                    closeOnNavigation: false,
                    data: {
                        dealId: this.deal.dealId,
                        jacketTypeId: this.deal.jacketTypeId,
                        customerName: this.deal.buyer.firstName + ' ' + this.deal.buyer.lastName,
                    },
                })
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((result) => {
                    if (result) {
                        this.snackbarService.openSnackbar(
                            'success',
                            `The jacket for <b>${this.deal.buyer.firstName + ' ' + this.deal.buyer.lastName}</b> has been deleted.`
                        );
                    }
                });
        }
    }

    openEditDocModal($event: Event, document: DdjDocument) {
        this.dialog
            .open(EditNameModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: false,
                data: {
                    title: 'Edit Document Info',
                    label: 'File Name',
                    value: { name: document.displayName },
                    isDealerOnly: document.isDealerOnly,
                    includeIsDealerOnlyCheckbox: this.canChangeIsDealerOnly(document),
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async (res) => {
                if (res?.update) {
                    // update the document's name

                    this.isLoading = true;
                    this.documentsService
                        .updateDocumentInfo(document, res.newName, res.isDealerOnly)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                this.isLoading = false;
                                if (error.error?.message !== undefined && error.error?.message !== null) {
                                    this.errorService.showError(error.error.message);
                                } else {
                                    this.errorService.showGenericError();
                                }
                                throw error;
                            })
                        )
                        .subscribe((response) => {
                            this.reloadDocs();
                        });
                }
            });
    }

    openEditNotesModal($event: Event) {
        $event.stopPropagation();
        this.dialog
            .open(EditNotesModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: true,
                data: {
                    title: 'Edit Deal Notes',
                    label: 'Notes',
                    value: { notes: this.deal.notes ?? '' },
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async (res) => {
                if (res?.update) {
                    this.isLoading = true;
                    this.dealService
                        .updateDealNotes(this.deal.dealId, this.deal.dealerId, res.notes.length === 0 ? null : res.notes)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                this.isLoading = false;
                                if (error.error?.message !== undefined && error.error?.message !== null) {
                                    this.errorService.showError(error.error.message);
                                } else {
                                    this.errorService.showGenericError();
                                }
                                throw error;
                            })
                        )
                        .subscribe((response) => {
                            // The Deal observable should be returned upon success
                            this.deal = response;
                            this.isLoading = false;
                        });
                }
            });
    }

    preventClick($event: Event): void {
        $event.stopPropagation();
    }

    deleteDocument($event: Event, doc: DdjDocument): void {
        $event.stopPropagation();

        this.isLoading = true;

        const esignConsentDocumentType =
            doc.internalDocumentTypeId === InternalDocumentTypeIds.BuyerESignConsent
                ? InternalDocumentTypeIds.BuyerESignConsent
                : InternalDocumentTypeIds.CobuyerESignConsent;

        if (!this.isInternalWithAdminPermission && doc.internalDocumentTypeId === esignConsentDocumentType) {
            // dealer users have extra restrictions around esign consent forms

            const custType: 'Buyer' | 'CoBuyer' = InternalDocumentTypeIds.BuyerESignConsent === doc.internalDocumentTypeId ? 'Buyer' : 'CoBuyer';
            const requirementKey: 'needsBuyerSignature' | 'needsCoBuyerSignature' = `needs${custType}Signature`;

            const docGroups: ('complete' | 'buyer' | 'cobuyer' | 'dealer' | 'signingCertInProcess' | 'interventionRequired' | 'notarizeQueue')[] = [
                'complete',
                'buyer',
                'cobuyer',
                'dealer',
                'signingCertInProcess',
                'interventionRequired',
                'notarizeQueue',
            ];
            // for each doc queue, check if this cust type has any documents they need to sign
            const docsForCustType = docGroups.flatMap((grp) => {
                const docs = this.dealDocs[grp];
                return docs.filter((d) => d.requirements[requirementKey] && d.internalDocumentTypeId !== esignConsentDocumentType);
            });

            if (docsForCustType.length > 0) {
                this.isLoading = false;
                // other docs for this cust type... don't delete until those are gone
                this.dialog.open(ErrorModalComponent, {
                    width: '700px',
                    closeOnNavigation: false,
                    data: {
                        title: 'Document Cannot Be Deleted',
                        body: `<p>This document, ${
                            doc.displayName
                        }, cannot be deleted from this Deal Jacket because there are documents that have been e-signed or are waiting for e-signing. Once those documents are deleted, you should be able to delete this document as well.</p><ul>${docsForCustType.map(
                            (d) => `<li>${d.displayName}</li>`
                        )}</ul>`,
                    },
                });
                return;
            }
        }

        this.documentsService
            .deleteDocument$(doc.dealerId, doc.dealId, doc.documentId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((result) => {
                if (result) this.reloadDocs();
            });
    }

    restoreDocument($event: Event, doc: DdjDocument): void {
        $event.stopPropagation();

        this.isLoading = true;
        // Prevent documents from being restored by those without permission
        if (!this.canModifyDocuments(false)) {
            this.isLoading = false;
            this.dialog.open(ErrorModalComponent, {
                width: '700px',
                closeOnNavigation: false,
                data: {
                    title: 'Cannot Restore This Document.',
                    message: 'You are not able to restore this document. If you need to have this document restored please contact support.',
                },
            });
        } else {
            this.documentsService
                .getDocLastQueueById(this.deal.dealId, this.deal.dealerId, doc.documentId)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    mergeMap(({ documentStatus: lastStatus, queueName: lastQueue }) => {
                        const formData: FormData = new FormData();
                        //User Fields
                        formData.append('dealerId', doc.dealerId.toString());
                        formData.append('documentStatusId', lastStatus.toString());

                        return this.documentsService.restoreDocument$(doc.dealId, doc.documentId, formData).pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                this.isLoading = false;
                                throw error;
                            })
                        );
                    })
                )
                .subscribe((docs) => {
                    this.reloadDocs();
                });
        }
    }

    signingCertInProgressClick($event: Event, doc: DdjDocument): void {
        $event.stopPropagation();
        if (this.isInternalWithAdminPermission) {
            this.internalCertInProgressClick($event, doc);
            return;
        }

        this.dialog
            .open(DocumentStatusModalComponent, {
                width: '700px',
                closeOnNavigation: true,
                data: {
                    document: this.documentsService.getDocById(doc.documentId),
                    message:
                        doc.documentStatusId === DocumentStatus.EnqueueCertification
                            ? 'Applying digital certificate to document. Document will be moved to Completed after the process is done.'
                            : 'Failed applying Digital Certificate. Please contact the help desk.',
                },
            })
            .beforeClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(() => this.reloadDocs());
    }

    internalCertInProgressClick($event: Event, doc: DdjDocument): void {
        this.dialog
            .open(InternalDocumentInterventionModalComponent, {
                width: '700px',
                closeOnNavigation: true,
                data: {
                    docId: doc.documentId,
                    dealId: doc.dealId,
                    docDisplayName: doc.displayName,
                    documentStatus: doc.documentStatusId,
                    deviceModalWidth: this.sharedService.getModalInnerContentWidth(ModalSize.Large),
                    deviceModalHeight: this.sharedService.getModalInnerContentHeight(ModalSize.Large),
                },
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((val) => {
                if (val) {
                    if (val.selectedDocumentStatus && val.type === 'status') {
                        this.documentsService
                            .changeDocumentStatus(val.selectedDocumentStatus, this.deal.dealerId, doc.dealId, doc.documentId)
                            .pipe(takeWhile(() => this.isComponentAlive))
                            .subscribe((x) => {
                                this.handleInternalDocumentIntervention(x);
                            });
                    } else if (val.type === 'reQueue') {
                        this.documentsService
                            .reSignDocument(this.deal.dealerId, doc.dealId, doc.documentId, val.signatureImagesOnDoc, val.overrideIsCertified)
                            .pipe(takeWhile(() => this.isComponentAlive))
                            .subscribe((x) => {
                                this.handleInternalDocumentIntervention(x);
                            });
                    }
                }
            });
    }

    handleInternalDocumentIntervention<T>(data: RpcApiResponse<T>): void {
        if (data) {
            if (data?.error) {
                this.errorService.showError(data?.error?.message);
            } else {
                this.reloadDocs();
            }
        } else {
            this.errorService.showGenericError();
        }
    }

    downloadDocuments(): void {
        const availableItems = [].concat(this.dealDocs?.buyer || [], this.dealDocs?.cobuyer || [], this.dealDocs?.dealer || [], this.dealDocs?.complete || []);
        const data: MultiSelectModalComponentProps = {
            // A document is considered available if:
            // - There is a fileName on the document
            // - The document is not of type Impact or Laser
            //     - If the document is marked as readyForSignatures we are good to display no matter the type
            availableItems: availableItems.filter(
                (d) =>
                    d.fileName &&
                    ((d.uploadTypeId !== FormDocumentUploadType.Print_From_Impact_Form && d.uploadTypeId !== FormDocumentUploadType.Print_From_PDF) ||
                        d.readyForSignatures)
            ),
            searchKey: 'displayName',
            returnedKey: 'documentId',
            title: 'Download Documents',
            description: 'Please select the documents you wish to download.',
            btnText: 'Download Documents',
            search: 'Search for a document using any term in the name',
            selectAll: true,
        };

        this.dialog
            .open(MultiSelectModalComponent, {
                width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                closeOnNavigation: true,
                panelClass: 'add-multiple-dialog',
                data,
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((result) => {
                if (result?.selectedItems) {
                    this.documentsService
                        .downloadDocs$(this.deal.dealId, false, result?.selectedItems)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((err) => {
                                console.error('error downloading: ', err);
                                this.isLoading = false;
                                throw err;
                            })
                        )
                        .subscribe(async (response) => {
                            const links = response.data;
                            const urls = [];
                            links.docs.forEach((doc) => {
                                urls.push(doc.filename);
                            });

                            for (let i = 0; i < urls.length; i++) {
                                await this.downloadService.downloadFile(urls[i]);
                            }
                            this.isLoading = false;
                        });
                }
            });
    }

    mergeDocuments(): void {
        this.router.navigate(['/dashboard', 'deals', this.deal.dealId, 'merge']);
    }

    notarizeDocuments() {
        this.router.navigate(['/dashboard', 'deals', this.deal.dealId, 'notarize']);
    }

    goToSendDocuments(route: string): void {
        this.router.navigate(['/dashboard', 'deals', this.deal.dealId, 'send', route]);
    }

    public isDocumentDisabled(doc: DdjDocument): boolean {
        if (
            (doc.internalDocumentTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument ||
                doc.internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument) &&
            !this.finalizeDocumentsEnabled
        ) {
            return true;
        }
        if (isIdentification(doc.internalDocumentTypeId) && !isMobileOrTablet()) {
            return true;
        }
        return false;
    }

    public isDocumentRouteOne(doc: DdjDocument): boolean {
        return this.vendorService.isDocumentForRouteOne(doc);
    }

    refreshDeal(dealId: number) {
        this.dealService
            .getDeal$(dealId, true)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((deal) => {
                if (deal?.buyer?.userId) {
                    this.checkUserLoginStatus(deal.buyer.userId, 'buyer');
                }

                if (deal?.coBuyer?.userId) {
                    this.checkUserLoginStatus(deal.coBuyer.userId, 'cobuyer');
                }
                this.deal = deal;
                this.setPageTitle();
            });
    }

    reloadDocs(): void {
        this.isLoading = true;
        this.documentsService
            .getDocsList$(this.deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showGenericError();
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((docs) => {
                this.sortDocumentQueues({
                    complete: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Complete),
                    buyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.BuyerQueue),
                    cobuyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.CoBuyerQueue),
                    dealer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.DealerQueue),
                    signingCertInProcess: docs.filter((doc) => doc.documentStatusId === DocumentStatus.EnqueueCertification),
                    interventionRequired: docs.filter((doc) => doc.documentStatusId === DocumentStatus.InterventionRequired),
                    deleted: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Deleted),
                    notarizeQueue: docs.filter((doc) => doc.documentStatusId === DocumentStatus.NotarizeQueue),
                });

                if (this.dealDocs.complete.some((doc) => isIdentification(doc.internalDocumentTypeId))) {
                    this.getIdVerificationResults();
                    this.getIdVerificationUtilization();
                } else {
                    this.buyerIdVerificationResults = null;
                    this.cobuyerIdVerificationResults = null;
                    this.isLoading = false;
                }

                this.updateLabels();
            });
    }

    public reloadPage(): void {
        location.reload();
    }

    public showPdfViewModal(dealId: number, documentId: number): void {
        this.isLoading = false;
        this.dialog.open(LightweightPdfViewModalComponent, {
            width: this.sharedService.getModalWidth(ModalSize.Large) + 'px',
            height: this.sharedService.getModalHeight(ModalSize.Large) + 'px',
            closeOnNavigation: false,
            data: {
                dealId: dealId,
                documentId: documentId,
                // only Open and Closed can be printed
                printButtonVisible: !this.isArchived,
            },
        });
    }

    signingOverride(document: DdjDocument): boolean {
        //Check for valid signing routing by assignee.
        //Non valid signing routing will be re-routed to view.
        //This will be checked a few times through out the signing process.
        const signingAccess = this.assigneeResolver.assigneeType;
        const changeDocStatusId = document.documentStatusId;
        switch (changeDocStatusId) {
            case DocumentStatus.DealerQueue.valueOf():
                return signingAccess !== Assignees.dealer;
            case DocumentStatus.BuyerQueue.valueOf():
                return signingAccess !== Assignees.buyer;
            case DocumentStatus.CoBuyerQueue.valueOf():
                return signingAccess !== Assignees.cobuyer;
            default:
                return false;
        }
    }

    docNotViewable(): void {
        this.dialog.open(ErrorModalComponent, {
            width: '700px',
            closeOnNavigation: false,
            data: {
                title: 'Document Not Viewable',
                message: 'No PDF or image has been uploaded to this slot, resulting in nothing being viewed. ',
            },
        });
    }

    routeTo(document: DdjDocument, type: DocActions = null): void {
        const userIsDealerAndNoDealerSignatureRequired = () => {
            return !document.requirements?.needsDealerSignature && (this.userService.isDealer || this.userService.isInternal);
        };
        const docIsClosed = () => {
            return this.documentsService.isClosedStatus(document.documentStatusId);
        };

        const isRouteOneDoc = () => {
            return this.vendorService.isDocumentForRouteOne(document);
        };

        //DDJ 3210 -- Reenable when other workflows are working. ccox 5/20/24
        const overrideRouteToView = this.signingOverride(document);

        if (DocumentStatus.Deleted === document.documentStatusId || this.isDeleted) {
            this.canModifyDocuments(false) && this.router.navigate(['/dashboard', 'deals', document.dealId, document.documentId, 'view']);
        } else if (docIsClosed()) {
            this.showPdfViewModal(document.dealId, document.documentId);
        } else if (type === DocActions.view) {
            this.router.navigate(['/dashboard', 'deals', document.dealId, document.documentId, 'view']);
        } else if (document?.fileName) {
            if (
                !this.finalizeDocumentsEnabled &&
                (document.internalDocumentTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument ||
                    document.internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument)
            ) {
                this.dialog.open(FinalDocModalComponent, {
                    width: this.deviceService.isDesktop() ? '700px' : '100vw',
                    closeOnNavigation: true,
                    data: {
                        dealerPending: this.documentsService.getDealerPending().length,
                        buyerPending: this.documentsService.getCustomerPending().length,
                        cobuyerPending: this.documentsService.getCoBuyerPending().length,
                        documentName: document.displayName,
                    },
                });
                return;
            } else {
                if (!this.isDealerSigningOutOfQueueEnabled) {
                    if (userIsDealerAndNoDealerSignatureRequired() || overrideRouteToView) {
                        this.showPdfViewModal(document.dealId, document.documentId);
                        return;
                    }
                }

                this.router.navigate(['/dashboard', 'deals', document.dealId, document.documentId, type ?? 'sign']);
            }
        } else if (isRouteOneDoc()) {
            // At the moment RouteOne documents are signature based only
            this.router.navigate(['/dashboard', 'deals', document.dealId, document.documentId, userIsDealerAndNoDealerSignatureRequired() ? 'view' : 'sign']);
        } else {
            let url: string = null;
            if (isIdentification(document.internalDocumentTypeId)) {
                if (isMobileOrTablet()) {
                    url = 'uploadIdn';
                } else {
                    this.showMobileOnlyUploadModal(document.internalDocumentTypeId);
                    return;
                }
            } else {
                url = 'upload';
            }
            this.router.navigate(['/dashboard', 'deals', document.dealId, document.documentId, url]);
        }
    }

    public async addFandIForm(): Promise<void> {
        try {
            this.isLoading = true;

            const availableDocs = await firstValueFrom(this.documentsService.getTemplateBasicList$(false, this.deal.dealerId));

            const existingDocs = [].concat(
                this.dealDocs?.buyer || [],
                this.dealDocs?.cobuyer || [],
                this.dealDocs?.dealer || [],
                this.dealDocs?.complete || []
            );

            const data: MultiSelectModalComponentProps = {
                availableItems: availableDocs,
                existingItems: existingDocs,
                searchKey: 'displayName',
                compareKey: 'templateDocumentId',
                returnedKey: 'documentId',
                title: 'Add F&I Forms',
                description:
                    'Below is a list of available forms. If you do not see the form you need, it has not been set up under F&I Forms. If you do not have access to create F&I Forms, contact your system administrator.',
                btnText: 'Add F&I Forms',
                search: 'Search for a form using any term in the form name',
            };

            this.isLoading = false;

            this.dialog
                .open(MultiSelectModalComponent, {
                    width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                    closeOnNavigation: true,
                    panelClass: 'add-multiple-dialog',
                    data,
                })
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(async (res) => {
                    if (res?.selectedItems) {
                        this.isLoading = true;
                        this.documentsService
                            .addDocumentsToDeal(res?.selectedItems, this.deal.dealerId, this.deal.dealId)
                            .pipe(takeWhile(() => this.isComponentAlive))
                            .subscribe((res) => {
                                this.documentsService
                                    .getDocsList$(this.deal.dealId)
                                    .pipe(takeWhile(() => this.isComponentAlive))
                                    .subscribe((docs) => {
                                        this.sortDocumentQueues({
                                            complete: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Complete),
                                            buyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.BuyerQueue),
                                            cobuyer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.CoBuyerQueue),
                                            dealer: docs.filter((doc) => doc.documentStatusId === DocumentStatus.DealerQueue),
                                            signingCertInProcess: docs.filter((doc) => doc.documentStatusId === DocumentStatus.EnqueueCertification),
                                            interventionRequired: docs.filter((doc) => doc.documentStatusId === DocumentStatus.InterventionRequired),
                                            deleted: docs.filter((doc) => doc.documentStatusId === DocumentStatus.Deleted),
                                            notarizeQueue: docs.filter((doc) => doc.documentStatusId === DocumentStatus.NotarizeQueue),
                                        });
                                        this.isLoading = false;
                                    });
                            });
                    }
                });
        } catch (ex) {
            console.error(`Error adding F&I forms to the deal, ${this.deal.dealId}`, ex);
            this.isLoading = false;
        }
    }

    public openDeleteDocumentsModal() {
        const data: MultiSelectModalComponentProps = {
            availableItems: [
                ...this.dealDocs.buyer,
                ...this.dealDocs.cobuyer,
                ...this.dealDocs.dealer,
                ...this.dealDocs.complete,
                ...this.dealDocs.interventionRequired,
            ],
            existingItems: [],
            searchKey: 'displayName',
            compareKey: 'documentId',
            returnedKey: 'documentId',
            title: 'Delete Documents',
            description: 'Select the items you would like to delete.',
            btnText: 'Delete Documents',
            search: 'Search for a document',
        };

        this.dialog
            .open(MultiSelectModalComponent, {
                width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                closeOnNavigation: true,
                panelClass: 'add-multiple-dialog',
                data,
            })
            .afterClosed()
            .subscribe((res) => {
                const documents: number[] = res.selectedItems;

                // if a esign consent is being deleted, add in all documents that that [co]buyer would have needed to sign, and delete all of them
                for (const doc of [...documents]) {
                    const buyerType: 'buyer' | 'coBuyer' = this.getEsignConsentType(doc);
                    if (buyerType) {
                        documents.push(...this.getDocumentsForBuyerType(buyerType).map((d) => d.documentId));
                    }
                }

                if (0 < documents.length) {
                    this.isLoading = true;
                    of(...new Set(documents))
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            map((documentId) => this.documentsService.deleteDocument$(this.deal.dealerId, this.deal.dealId, documentId)),
                            mergeAll()
                        )
                        .subscribe(() => {
                            this.reloadDocs();
                            this.isLoading = false;
                        });
                }
            });
    }

    private getEsignConsentType(documentId: number): 'buyer' | 'coBuyer' {
        const documents = this.getDocuments();
        for (const doc of documents) {
            if (doc.documentId === documentId) {
                if (InternalDocumentTypeIds.BuyerESignConsent === doc.internalDocumentTypeId) {
                    return 'buyer';
                }
                if (InternalDocumentTypeIds.CobuyerESignConsent === doc.internalDocumentTypeId) {
                    return 'coBuyer';
                }
            }
        }

        return null;
    }

    private getDocumentsForBuyerType(buyerType: 'buyer' | 'coBuyer') {
        const tc = (buyerType[0].toUpperCase() + buyerType.slice(1)) as 'Buyer' | 'CoBuyer';
        const flag: 'needsBuyerSignature' | 'needsCoBuyerSignature' = `needs${tc}Signature`;
        return this.getDocuments().filter((doc) => doc.requirements[flag]);
    }

    private getDocuments() {
        return [...this.dealDocs.buyer, ...this.dealDocs.cobuyer, ...this.dealDocs.dealer, ...this.dealDocs.complete, ...this.dealDocs.interventionRequired];
    }

    private showMobileOnlyUploadModal(internalDocumentTypeId: InternalDocumentTypeIds) {
        this.dialog.open(MobileOnlyUploadModalComponent, {
            width: '700px',
            closeOnNavigation: true,
            data: {
                internalDocumentTypeId: internalDocumentTypeId,
            },
        });
    }

    public sendNotification(type: number): void {
        this.isLoading = true;
        this.dealService
            .sendNotification(this.deal.dealId, type)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.isLoading = false;
                    this.errorService.showGenericError();
                    throw error;
                })
            )
            .subscribe((val) => {
                this.isLoading = false;
                if (val) {
                    if (type === 1) {
                        const buyerName =
                            !this.deal?.buyer?.firstName && !this.deal?.buyer?.lastName
                                ? 'the customer'
                                : `${this.deal?.buyer?.firstName} ${this.deal?.buyer?.lastName}`;
                        this.snackbarService.openSnackbar('success', `Notification has been sent to <b>${buyerName}</b>.`);
                    } else {
                        const coBuyerName =
                            !this.deal?.coBuyer?.firstName && !this.deal?.coBuyer?.lastName
                                ? 'the cobuyer'
                                : `${this.deal?.coBuyer?.firstName} ${this.deal?.coBuyer?.lastName}`;
                        this.snackbarService.openSnackbar('success', `Notification has been sent to <b>${coBuyerName}</b>.`);
                    }
                }
            });
    }

    public showMissingImage($event): void {
        showMissingImage($event);
    }

    public viewAuditTrail($event: Event, doc: DdjDocument): void {
        $event.stopPropagation();
        this.isLoading = true;

        this.dealService
            .getAuditTrail(doc.dealerId, doc.dealId, true, doc.documentId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showError(
                        `We don't have any data for this audit trail. If you think this is an error, please contact ${this.tenantSupportPhone}`
                    );
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((trail) => {
                this.isLoading = false;

                this.dialog.open(AuditTrailModalComponent, {
                    width: '900px',
                    closeOnNavigation: true,
                    data: {
                        doc,
                        trail,
                        deal: this.deal,
                    },
                });
            });
    }

    public viewNotesAuditTrail($event: Event): void {
        $event.stopPropagation();
        this.isLoading = true;

        this.dealService
            .getNotesAuditTrail(this.deal.dealId)
            .pipe(
                takeWhile(() => this.isComponentAlive),
                catchError((error) => {
                    this.errorService.showError(
                        `We don't have any data for this audit trail. If you think this is an error, please contact ${this.tenantSupportPhone}`
                    );
                    this.isLoading = false;
                    throw error;
                })
            )
            .subscribe((trail) => {
                this.isLoading = false;

                this.dialog.open(AuditTrailModalComponent, {
                    width: '900px',
                    closeOnNavigation: true,
                    data: {
                        trail,
                        deal: this.deal,
                    },
                });
            });
    }

    public getRIC() {
        const dialogRef = this.dialog.open(R1JacketModalComponent, {
            width: '700px',
            closeOnNavigation: false,
            disableClose: true,
            data: {
                deal: this.deal,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((res) => {
                if (res?.refresh) {
                    this.reloadDocs();
                }
            });
    }

    public addCoBuyer() {
        this.dialog
            .open(AddCobuyerModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: false,
                data: this.deal,
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(async (response) => {
                if (response?.valid && response?.cobuyer) {
                    try {
                        this.isLoading = true;
                        // Add the Co-Buyer to the Jacket
                        const updatedDeal = await firstValueFrom(this.dealService.addCoBuyerToJacket(this.deal.dealId, response?.cobuyer));

                        // Force refresh the deal on the dealService in case the user does
                        // not refresh the page
                        if (updatedDeal.dealId) {
                            this.dealService
                                .getDeal$(updatedDeal.dealId, true)
                                .pipe(takeWhile(() => this.isComponentAlive))
                                .subscribe((deal) => {
                                    this.deal = deal;
                                });
                        }

                        this.reloadDocs();
                        this.successModalService.showSuccess({
                            title: 'Co-Buyer Added Successfully!',
                            content: 'The requested Co-Buyer has been added to the deal! A welcome message has been sent to them on how to proceed.',
                        });
                    } catch (ex) {
                        console.error(`Could not add co-buyer to deal, ${this.deal.dealId}.`);
                        this.isLoading = false;
                    }
                }
            });
    }
}
